// Batch 26 · Add-on 1 — "Search for unrecorded liabilities" worksheet (<head>-07 on every liability head). V1.
// Opened by HeadFolders.jsx with ?open=<head>-07 (3101–3108, 3201–3212). Its own file (big-file rule) and its own EN + AR text.
// Population upload → selection (threshold or ticked) → per row: relates to the year? recorded at year end? omitted amount,
// supporting documents → omitted rows to Misstatements → totals, conclusion, sign-off. Dates DD-MMM-YYYY. The standards are
// named without paragraph numbers (project rule: only paragraph numbers checked against the standard's text are shown).
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CtNotSetNote from "@/components/CtNotSetNote";   // Batch 27 · D (M37)
import SurlJournalsTab, { JTXT, RouteToggle } from "@/components/SurlJournalsTab";   // Batch 27 · Add-on 3 · D — route 2
import useMissingFields, { FieldMessage, missingFromError } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1
import { useSearchParams } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import SignOffBar from "@/components/SignOffBar";
import SourceBadge from "@/components/SourceBadge";
import LoadingNote from "@/components/LoadingNote";
import DateInput from "@/components/DateInput";
import Ref from "@/components/Ref";
import { fmtNumber } from "@/components/Amount";
import { fmtDate } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { toast } from "sonner";
import SaveCancelBar from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";
import { saveRowFirst } from "@/lib/saveThenSend";   // STEP 11d · A9: Send saves a changed row first
import { useTableDraft } from "@/lib/useDrafts";
import { confirmLeave } from "@/lib/unsavedChanges";   // the registry also knows the journals tab's table (SurlJournalsTab)
import RecordedYe, { SurlEntries } from "@/components/SurlRecordedYe";   // Batch 29 · M64 (c): route 1 has the same gap; Batch 31 · item 9 (M91) entries
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { SurlVatFields, SurlLinkActions } from "@/components/SurlRowB34";   // Batch 34 · Z-15 / Z-16
import { LinkedDocsViewButton } from "@/components/VouchingDocViewer";   // Batch 34 · B34-11
import { AnswerMissingNote } from "@/components/SurlAgingB34L";   // Batch 34 · S18
import TbOnlyNotice from "@/components/TbOnlyNotice";   // PR 3 · rule 1 (B-1a): TB-only → the GL checks are not applicable

// Step 11d-3: the rule lives in lib/worksheetLinks.js (Head Folders reads it without loading this dialog); re-exported here
import { SURL_CODE_RX } from "@/lib/worksheetLinks";
export { SURL_CODE_RX };
const fmt = (n) => fmtNumber(Number(n || 0));
const DOC_TYPES = ["purchase_invoice", "subcontractor_certificate", "progress_certificate", "bank_statement"];

export const TXT = {
  en: {
    title: "Search for unrecorded liabilities",
    std: "Assertion tested: completeness of liabilities (ISA 315). Evidence from after the period end: subsequent events (ISA 560).",
    periodEnd: "Period end", window: "Window", windowDays: "Days after the period end", windowHint: "Default 60 days; up to the report date", windowCapped: "cut at the report date",
    reportDate: "Report date", threshold: "Threshold", thresholdSource: "Source", srcClearlyTrivial: "105(a) clearly trivial", srcSetBy: "Set by", theAuditor: "the auditor", reason: "Reason for the change",
    saveThreshold: "Save threshold", resetThreshold: "Back to the default", save: "Save",
    upload: "Upload post-year-end payments and invoices", uploadCols: "Columns: Date, Voucher, Party, Invoice No, Invoice Date, Goods / Service Date (or period to), Amount, Description, Source (payment | invoice). Files of the cut-off post-year-end upload (Date, Voucher, Account, Amount, Description) also work.",
    uploaded: "Uploaded", rowsKept: "rows in the window", rowsInFile: "rows in the file", carried: "answers kept from the previous upload", jobQueued: "The file is large; it is read in the background.",
    agingSample: "The AP aging upload (3203-06) holds only its first 50 lines (an older upload): upload it again to match every invoice.",
    agingNone: "No AP aging upload (3203-06): matching uses the general ledger only.",
    showAll: "Show every row", showSelected: "Show the selected rows",
    cols: { sel: "Selected", date: "Date", voucher: "Voucher", party: "Party", inv: "Invoice No", invDate: "Invoice date", goods: "Goods / service date",
            amount: "Amount", source: "Source", relates: "Relates to the year?", recorded: "Recorded at year end?", omitted: "Omitted amount",
            result: "Result", otherSide: "Other side", docs: "Supporting documents", action: "" },
    byThreshold: "≥ threshold", tick: "Select",
    rel: { "": "To decide", yes: "Yes", no: "No", part: "Part" }, proposed: "Proposed", partAmount: "Part relating to the year",
    match: { none: "Not recorded", proposed: "Proposed match", accepted: "Match accepted", rejected: "Match rejected" },
    fromAging: "AP aging line", fromGl: "GL credit", glNote: "Supplier has year-end entries — no matching amount", accept: "Accept", reject: "Reject", undo: "Undo",
    res: { recorded: "Recorded", omitted: "Omitted", next_period: "Relates to next period", part_omitted: "Part omitted", to_confirm: "To confirm", not_selected: "—" },
    expense: "Expense understated", asset: "Asset understated", send: "Send to Misstatements", sendAgain: "Send again (refresh)",
    sent: "Sent", needsRefresh: "Needs refresh", link: "Link", linkTitle: "Link supporting documents",
    totals: "Summary", tPopulation: "Population", tSelected: "Selected", tTested: "Tested", tRecorded: "Recorded", tOmitted: "Omitted",
    tSent: "Sent to Misstatements", tNext: "Next period", tConfirm: "To confirm", count: "count", amountW: "amount",
    conclusion: "Conclusion", conclusionDate: "Conclusion date", preparer: "Preparer", reviewer: "Reviewer", markComplete: "Mark complete",
    reopen: "Reopen", fromBar: "From the sign-off bar", notSigned: "Not signed", completed: "Completed", cannotComplete: "Not yet complete:", gate: { no_route: "Perform at least one route: upload the payments and invoices or the subsequent journals.", payments_not_performed_reason: "Give the reason the payments and invoices route was not performed.", journals_not_performed_reason: "Give the reason the subsequent journals route was not performed.", journals_to_confirm: "{n} selected subsequent journal(s) still to confirm.", journals_needs_refresh: "{n} subsequent-journal misstatement(s) need a refresh.", journals_not_sent: "Send every omitted subsequent journal to Misstatements.", no_upload: "Upload the post-year-end payments and invoices first.", to_confirm: "{n} selected row(s) still to confirm.", needs_refresh: "{n} misstatement(s) need a refresh.", not_sent: "Send every omitted row to Misstatements.", no_conclusion: "Write the conclusion.", no_preparer: "Preparer sign-off is missing.", no_reviewer: "Reviewer sign-off is missing." },
    noRows: "Upload the file to start.", empty: "—",
  },
  ar: {
    title: "البحث عن الالتزامات غير المسجلة",
    std: "الإثبات المختبر: اكتمال الالتزامات (المعيار الدولي للمراجعة 315). أدلة ما بعد نهاية الفترة: الأحداث اللاحقة (المعيار الدولي للمراجعة 560).",
    periodEnd: "نهاية الفترة", window: "النافذة", windowDays: "أيام بعد نهاية الفترة", windowHint: "الافتراضي 60 يوماً؛ حتى تاريخ التقرير", windowCapped: "مقصورة على تاريخ التقرير",
    reportDate: "تاريخ التقرير", threshold: "حد الاختيار", thresholdSource: "المصدر", srcClearlyTrivial: "الحد التافه في 105أ", srcSetBy: "حدده", theAuditor: "المدقق", reason: "سبب التغيير",
    saveThreshold: "حفظ الحد", resetThreshold: "العودة إلى الافتراضي", save: "حفظ",
    upload: "رفع مدفوعات وفواتير ما بعد نهاية السنة", uploadCols: "الأعمدة: التاريخ، رقم القيد، الطرف، رقم الفاتورة، تاريخ الفاتورة، تاريخ البضاعة / الخدمة (أو الفترة حتى)، المبلغ، الوصف، المصدر (دفعة | فاتورة). تعمل أيضاً ملفات رفع ما بعد نهاية السنة لاختبار فصل الفترات (التاريخ، رقم القيد، الحساب، المبلغ، الوصف).",
    uploaded: "تم الرفع", rowsKept: "صفاً ضمن النافذة", rowsInFile: "صفاً في الملف", carried: "إجابة محفوظة من الرفع السابق", jobQueued: "الملف كبير؛ تتم قراءته في الخلفية.",
    agingSample: "يحتوي رفع أعمار الذمم الدائنة (3203-06) على أول 50 سطراً فقط (رفع أقدم): ارفعه مجدداً لمطابقة كل الفواتير.",
    agingNone: "لا يوجد رفع لأعمار الذمم الدائنة (3203-06): تتم المطابقة مع دفتر الأستاذ العام فقط.",
    showAll: "عرض كل الصفوف", showSelected: "عرض الصفوف المختارة",
    cols: { sel: "مختار", date: "التاريخ", voucher: "رقم القيد", party: "الطرف", inv: "رقم الفاتورة", invDate: "تاريخ الفاتورة", goods: "تاريخ البضاعة / الخدمة",
            amount: "المبلغ", source: "المصدر", relates: "يخص السنة؟", recorded: "مسجل في نهاية السنة؟", omitted: "المبلغ المحذوف",
            result: "النتيجة", otherSide: "الطرف المقابل", docs: "المستندات المؤيدة", action: "" },
    byThreshold: "≥ الحد", tick: "اختيار",
    rel: { "": "للتحديد", yes: "نعم", no: "لا", part: "جزئياً" }, proposed: "مقترح", partAmount: "الجزء الخاص بالسنة",
    match: { none: "غير مسجل", proposed: "مطابقة مقترحة", accepted: "تم قبول المطابقة", rejected: "تم رفض المطابقة" },
    fromAging: "سطر أعمار الذمم الدائنة", fromGl: "قيد دائن في الأستاذ العام", glNote: "للمورد قيود حتى نهاية السنة — لا يوجد مبلغ مطابق", accept: "قبول", reject: "رفض", undo: "تراجع",
    res: { recorded: "مسجل", omitted: "محذوف", next_period: "يخص الفترة التالية", part_omitted: "محذوف جزئياً", to_confirm: "للتأكيد", not_selected: "—" },
    expense: "مصروف بأقل من قيمته", asset: "أصل بأقل من قيمته", send: "إرسال إلى التحريفات", sendAgain: "إرسال مجدداً (تحديث)",
    sent: "تم الإرسال", needsRefresh: "يحتاج إلى تحديث", link: "ربط", linkTitle: "ربط المستندات المؤيدة",
    totals: "الملخص", tPopulation: "المجتمع", tSelected: "المختار", tTested: "المختبر", tRecorded: "المسجل", tOmitted: "المحذوف",
    tSent: "المرسل إلى التحريفات", tNext: "الفترة التالية", tConfirm: "للتأكيد", count: "العدد", amountW: "المبلغ",
    conclusion: "الخلاصة", conclusionDate: "تاريخ الخلاصة", preparer: "المُعِد", reviewer: "المراجع", markComplete: "تحديد كمكتمل",
    reopen: "إعادة الفتح", fromBar: "من شريط التوقيعات", notSigned: "لم يُوقَّع", completed: "مكتمل", cannotComplete: "لم يكتمل بعد:", gate: { no_route: "نفّذ طريقة واحدة على الأقل: ارفع المدفوعات والفواتير أو القيود اللاحقة.", payments_not_performed_reason: "اذكر سبب عدم تنفيذ طريقة المدفوعات والفواتير.", journals_not_performed_reason: "اذكر سبب عدم تنفيذ طريقة القيود اللاحقة.", journals_to_confirm: "{n} من القيود اللاحقة المختارة لم تُؤكَّد بعد.", journals_needs_refresh: "{n} من تحريفات القيود اللاحقة تحتاج إلى تحديث.", journals_not_sent: "أرسل كل قيد لاحق محذوف إلى التحريفات.", no_upload: "ارفع مدفوعات وفواتير ما بعد نهاية السنة أولاً.", to_confirm: "{n} من الصفوف المختارة لم تُؤكَّد بعد.", needs_refresh: "{n} من التحريفات تحتاج إلى تحديث.", not_sent: "أرسل كل صف محذوف إلى التحريفات.", no_conclusion: "اكتب الاستنتاج.", no_preparer: "توقيع المُعِد غير موجود.", no_reviewer: "توقيع المراجع غير موجود." },
    noRows: "ارفع الملف للبدء.", empty: "—",
  },
};

const RES_CLASS = { recorded: "bg-emerald-100 text-emerald-800", omitted: "bg-red-100 text-red-800", part_omitted: "bg-red-100 text-red-800",
                    next_period: "bg-slate-100 text-slate-700", to_confirm: "bg-amber-100 text-amber-800", not_selected: "text-muted-foreground" };

// Step 11d · add-on: adjustable columns of the payments rows (the keys in display order, the default / minimum widths)
const SURL_COL_KEYS = ["sel", "date", "voucher", "party", "inv", "invDate", "goods", "amount", "relates", "recorded", "omitted", "result", "otherSide", "docs", "action"];
const SURL_COL_W = [56, 100, 120, 180, 110, 100, 110, 120, 120, 120, 120, 140, 160, 120, 130];
const SURL_COL_MIN = [44, 96, 90, 110, 90, 96, 96, 100, 96, 96, 96, 100, 110, 90, 100];

export default function SurlWorksheetDialog() {
  const { currentEngId } = useEngagement();
  const surlCols = useResizableColumns({ storageKey: "surl-payments", defaults: SURL_COL_W, mins: SURL_COL_MIN });
  const { lang } = useI18n();
  const L = TXT[lang === "ar" ? "ar" : "en"];
  const J = JTXT[lang === "ar" ? "ar" : "en"];
  const [params, setParams] = useSearchParams();
  const code = params.get("open") || "";
  const focusRow = params.get("row") || "";
  const [tab, setTab] = useState(params.get("tab") === "journals" ? "journals" : "payments");   // Batch 27 · Add-on 3 · D
  const isSurl = SURL_CODE_RX.test(code);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlySelected, setOnlySelected] = useState(true);
  const [busy, setBusy] = useState("");
  const [uploadErr, setUploadErr] = useState("");      // Batch 30 · M81
  const [draft, setDraft] = useState({});
  const [linkRow, setLinkRow] = useState(null);

  const load = useCallback(() => {
    if (!isSurl || !currentEngId) { setData(null); return; }
    setLoading(true);
    api.get(`/engagements/${currentEngId}/surl/${code}`, { _silent: true })
      .then((r) => { setData(r.data); setDraft({}); })
      .catch((e) => { setData(null); toast.error(toErrMsg(e)); })
      .finally(() => setLoading(false));
  }, [isSurl, code, currentEngId]);
  useEffect(() => { load(); }, [load]);
  const mf = useMissingFields(lang);     // Batch 27 · Add-on 1
  // Batch 28 · Follow-up 1 · F2: the settings (window days, threshold + reason) and the conclusion are one form draft — the
  // Save / Cancel bar sends the changed fields in one PATCH (they had three Save buttons); "Back to the default" stays an
  // action. The population rows are a table draft (one bar; Save sends only the changed rows, one PATCH each; a failed row
  // keeps its error). Upload, link documents, Send to Misstatements, mark complete / reopen and the sign-offs stay immediate.
  const base = `/engagements/${currentEngId}/surl/${code}`;
  const refresh = async () => { const r = await api.get(base, { _silent: true }); setData(r.data); };
  const td = useTableDraft({
    rows: asArray(data?.worksheet?.population?.rows), getId: (r) => r.row_id,
    saveRow: async (rowId, p) => {
      const body = { ...p };
      if ("part_amount" in body) { body.part_amount = Number(body.part_amount || 0); body.relates = "part"; }
      if ("vat_amount" in body && body.vat_amount === "") delete body.vat_amount;      // Batch 34 · Z-15: a cleared box sends nothing
      try { await api.patch(`${base}/rows/${rowId}`, body); mf.clear(`part_amount.${rowId}`); }
      catch (e) {
        const list = missingFromError(e).map((m) => ({ ...m, field: `${m.field}.${rowId}` }));   // the row's own field
        if (list.length) mf.apply({ missing_fields: list });
        throw e;
      }
    },
    reload: refresh,
  });

  if (!isSurl) return null;
  const formDirty = Object.keys(draft).some((k) => k !== "threshold_reason");
  const close = async () => {
    if (!(await confirmLeave(lang, "discard"))) return;
    const n = new URLSearchParams(params); n.delete("open"); n.delete("row"); setParams(n, { replace: true });
  };
  const ws = data?.worksheet || {};
  const pop = ws.population || null;
  const rows = asArray(pop?.rows);
  const shown = onlySelected ? rows.filter((r) => r.eval?.selected) : rows;
  const tot = data?.totals || {};
  const done = !!ws.completed;

  const patchWs = async (body, okMsg) => {
    setBusy("ws");
    try { const r = await api.patch(base, body); setData(r.data); setDraft({}); mf.reset(); if (okMsg) toast.success(okMsg); }
    catch (e) { if (!mf.apply(e)) toast.error(toErrMsg(e)); } finally { setBusy(""); }
  };
  const patchRow = (rowId, body) => { Object.entries(body).forEach(([k, v]) => td.edit(rowId, k, v)); };
  const saveForm = () => {
    const body = {};
    if (draft.window_days !== undefined) body.window_days = Number(draft.window_days);
    if (draft.threshold_amount !== undefined) { body.threshold_amount = Number(draft.threshold_amount); body.threshold_reason = draft.threshold_reason || ""; }
    ["conclusion", "conclusion_date"].forEach((k) => { if (draft[k] !== undefined) body[k] = draft[k]; });
    return patchWs(body);
  };
  const switchTab = async (k) => { if (k === tab) return; if (!(await confirmLeave(lang, "discard"))) return; td.cancel(); setTab(k); };
  const send = async (rowId) => {
    setBusy(rowId);
    // STEP 11d · A9 (find 30): a changed row is saved first; a failed save shows its error and nothing is sent
    if (!(await saveRowFirst(td, rowId, lang))) { setBusy(""); return; }
    try { await api.post(`${base}/rows/${rowId}/send-to-misstatements`); await refresh(); toast.success(L.sent); }
    catch (e) {      // Batch 29 · M65: already in Misstatements from another worksheet → offer the link
      try { if (await offerLink(e, { engId: currentEngId, target: { type: "surl_row", code, row_id: rowId }, lang })) { await refresh(); return; } }
      catch (e2) { toast.error(toErrMsg(e2)); return; }
      toast.error(toErrMsg(e));
    } finally { setBusy(""); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy("upload"); setUploadErr("");
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`${base}/population-upload`, fd, { _silent: true });
      if (r.data.mode === "job") {
        toast.message(L.jobQueued);
        for (let i = 0; i < 600; i += 1) {
          const j = await api.get(`/jobs/${r.data.job_id}`, { _silent: true });
          if (["done", "failed"].includes(j.data.status)) { if (j.data.status === "failed") setUploadErr((lang === "ar" && j.data.error_ar) || j.data.error || "failed"); break; }   // Batch 31 · item 25: screen language only
          await new Promise((res) => setTimeout(res, 1000));
        }
      } else {
        toast.success(`${L.uploaded}: ${r.data.kept} ${L.rowsKept}`);
      }
      await refresh();
    } catch (e) {
      // Batch 30 · M81: a refused upload (no row kept) stays on the page, in the screen's language — the rows already there are kept
      const d = e?.response?.data || {};
      if (d.error_code === "SURL_UPLOAD_NO_ROWS") setUploadErr((lang === "ar" && d.detail_ar) || d.detail || toErrMsg(e));
      else toast.error(toErrMsg(e));
    } finally { setBusy(""); }
  };

  return (
    <FullScreenDialog open={true} onClose={close} testid="surl-dialog"
                      title={<span data-testid={`surl-title-${code}`}><Ref>{code}</Ref> {L.title} <SourceBadge source={data?.source} className="ms-2 align-middle" /></span>}>
      {data && <SignOffBar itemType="hf_worksheet" itemKey={code} refreshKey={ws.updated_at} onChange={refresh} missing={mf.missing} saveFirst={formDirty || td.dirty} />}
      {loading && <div className="p-6"><LoadingNote active what={L.title} /></div>}
      {!loading && data && (
        <div className="space-y-4 text-sm" data-testid="surl-body">
          <p className="text-xs text-muted-foreground" data-testid="surl-std">{L.std}</p>
          <TbOnlyNotice status={data.gl_status} testid="surl-tb-only" />
          <Settings data={data} L={L} draft={draft} setDraft={setDraft} patchWs={patchWs} busy={busy === "ws"} done={done} mf={mf} />
          {/* Batch 27 · Add-on 3 · D: two sources on the same worksheet */}
          <div className="flex gap-1 border-b" role="tablist" data-testid="surl-tabs">
            {[["payments", J.tabPayments], ["journals", J.tabJournals]].map(([k, lab]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`surl-tab-${k}`}
                      className={`px-3 py-1.5 text-xs ${tab === k ? "border-b-2 border-emerald-600 font-medium" : "text-muted-foreground"}`}
                      onClick={() => switchTab(k)}>{lab}</button>))}
          </div>
          {tab === "journals" && <SurlJournalsTab engId={currentEngId} code={code} data={data} J={J} done={done} refresh={refresh} mf={mf}
                                                  openLink={(v) => setLinkRow(v)} />}
          {tab === "payments" && (<>
          <div className="rounded border p-3" data-testid="surl-upload">
            <label className="block font-medium">{L.upload}</label>
            <p className="mt-1 text-xs text-muted-foreground">{L.uploadCols}</p>
            <input type="file" accept=".xlsx,.csv" disabled={done || busy === "upload"} className="mt-2 text-xs"
                   data-testid="surl-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
            {uploadErr && (
              <p className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-200"
                 role="alert" data-testid="surl-upload-error">{uploadErr}</p>
            )}
            {pop && (
              <p className="mt-2 text-xs" data-testid="surl-upload-info">
                {L.uploaded}: {pop.filename} · {fmtDate(pop.uploaded_at)} · {pop.rows_in_file} {L.rowsInFile} · {pop.counts?.in_window ?? rows.length} {L.rowsKept}
                {pop.decisions_carried ? ` · ${pop.decisions_carried} ${L.carried}` : ""}
              </p>
            )}
            {data.aging_extent === "sample" && <p className="mt-1 text-xs text-amber-700" data-testid="surl-aging-sample">{L.agingSample}</p>}
            {data.aging_extent === "none" && <p className="mt-1 text-xs text-muted-foreground" data-testid="surl-aging-none">{L.agingNone}</p>}
            <div className="mt-2"><RouteToggle engId={currentEngId} code={code} route="payments" state={data.routes?.payments} J={J} done={done} onSaved={refresh} mf={mf} /></div>
          </div>
          {!rows.length ? <p className="text-muted-foreground" data-testid="surl-empty">{L.noRows}</p> : (
            <>
              <div className="flex items-center gap-3">
                <button type="button" className="text-xs underline" data-testid="surl-toggle-rows" onClick={() => setOnlySelected((v) => !v)}>
                  {onlySelected ? L.showAll : L.showSelected}
                </button>
              </div>
              <div className="overflow-x-auto rounded border">
                <ResizableTable cols={surlCols} className="w-full text-xs" data-testid="surl-rows">
                  <thead className="bg-muted/50"><tr>
                    {SURL_COL_KEYS.map((k, i) => <ResizableTh key={k} index={i} className="px-2 py-1 text-start font-medium">{L.cols[k]}</ResizableTh>)}
                  </tr></thead>
                  <tbody>{shown.map((r) => (
                    <Row key={r.row_id} r={td.rowValue(r)} saved={r} draft={td} L={L} busy={busy === r.row_id || td.saving} done={done} focus={focusRow === r.row_id}
                         patchRow={patchRow} send={send} openLink={() => setLinkRow(r)} mf={mf} engId={currentEngId} code={code} refresh={refresh} />
                  ))}</tbody>
                </ResizableTable>
              </div>
              <TableSaveBar draft={td} lang={lang} testid="surl-table-save" />
            </>
          )}
          <Totals tot={tot} L={L} />
          </>)}
          <Conclusion ws={ws} L={L} draft={draft} setDraft={setDraft} patchWs={patchWs} busy={busy === "ws"} gate={data.can_mark_complete} mf={mf}
                      formDirty={formDirty || td.dirty} />
          <SaveCancelBar dirty={formDirty} saving={busy === "ws"} onSave={saveForm} onCancel={() => { setDraft({}); mf.reset(); }} lang={lang} testid="surl-save-bar" />
          {linkRow && <LinkDocs engId={currentEngId} row={linkRow} L={L} onClose={() => setLinkRow(null)}
                                onSave={async (ids) => {
                                  if (linkRow._source === "journals") { await api.patch(`${base}/journals/${linkRow.row_id}`, { document_ids: ids }); await refresh(); }
                                  else {
                                    try { await api.patch(`${base}/rows/${linkRow.row_id}`, { document_ids: ids }); await refresh(); }
                                    catch (e) { toast.error(toErrMsg(e)); }
                                  }
                                  setLinkRow(null); }} />}
        </div>
      )}
    </FullScreenDialog>
  );
}

function Settings({ data, L, draft, setDraft, patchWs, busy, done, mf }) {
  const th = data.threshold || {};
  const ws = data.worksheet || {};
  const days = draft.window_days ?? ws.window_days ?? 60;
  const amount = draft.threshold_amount ?? th.amount;
  return (
    <div className="grid gap-3 rounded border p-3 md:grid-cols-3" data-testid="surl-settings">
      <div>
        <div className="text-xs text-muted-foreground">{L.periodEnd}</div>
        <div data-testid="surl-period-end">{fmtDate(data.period_end)}</div>
        {data.window && <div className="mt-1 text-xs" data-testid="surl-window">{L.window}: {fmtDate(data.window.from)} – {fmtDate(data.window.to)}{data.window.capped_at_report_date ? ` (${L.windowCapped})` : ""}</div>}
        {data.report_date && <div className="text-xs text-muted-foreground">{L.reportDate}: {fmtDate(data.report_date)}</div>}
      </div>
      <div>
        <label className="block text-xs text-muted-foreground">{L.windowDays}</label>
        <div className="mt-1 flex gap-2">
          <input type="number" min={1} value={days} disabled={done} data-testid="surl-window-days"
                 onChange={(e) => setDraft((d) => ({ ...d, window_days: e.target.value }))} className="w-24 rounded border px-2 py-1 text-xs" />
        </div>
        <div className="text-[11px] text-muted-foreground">{L.windowHint}</div>
      </div>
      <div>
        <label className="block text-xs text-muted-foreground">{L.threshold}</label>
        <div className="mt-1 flex flex-wrap gap-2">
          <input type="number" min={0} step="0.01" value={amount ?? ""} disabled={done} data-testid="surl-threshold"
                 onChange={(e) => setDraft((d) => ({ ...d, threshold_amount: e.target.value }))} className="w-32 rounded border px-2 py-1 text-xs" />
          <input type="text" placeholder={L.reason} value={draft.threshold_reason || ""} disabled={done} data-testid="surl-threshold-reason"
                 onChange={(e) => { setDraft((d) => ({ ...d, threshold_reason: e.target.value })); mf?.filled("threshold_reason", e.target.value); }}
                 {...(mf ? mf.props("threshold_reason", "min-w-0 flex-1 rounded border px-2 py-1 text-xs") : { className: "min-w-0 flex-1 rounded border px-2 py-1 text-xs" })} />
        </div>
        <FieldMessage mf={mf} field="threshold_reason" testid="surl-threshold-reason-missing" />
        <div className="mt-1 flex gap-2">
          <button type="button" disabled={done || busy} className="text-xs underline" data-testid="surl-threshold-reset"
                  onClick={() => patchWs({ threshold_reset: true, threshold_reason: draft.threshold_reason || "" })}>{L.resetThreshold}</button>
        </div>
        {!th.default?.set && <CtNotSetNote value={th.default?.amount} testid="surl-ct-not-set" />}
        <div className="text-[11px] text-muted-foreground" data-testid="surl-threshold-source">
          {L.thresholdSource}: {thresholdSourceText(th, L)}{th.reason ? ` — ${th.reason}` : ""} · {fmt(th.default?.amount)}
        </div>
      </div>
    </div>
  );
}

function Row({ r, saved, draft, L, busy, done, focus, patchRow, send, openLink, mf, engId, code, refresh }) {
  const ev = r.eval || {};
  const m = ev.proposed_match;
  const dis = done || busy;
  const partRef = React.useRef(null);
  const changed = draft.isChanged(r.row_id);
  // F2: a row ticked in the draft shows its decisions at once; the match status shows the drafted decision until saved
  const selected = !!ev.selected || !!r.selected_manual;
  const dec = r.match_decision !== saved.match_decision ? r.match_decision : null;
  const matchStatus = dec === null || dec === undefined ? ev.match_status : (dec === "accept" ? "accepted" : dec === "reject" ? "rejected" : "proposed");
  return (
    <tr className={`border-t align-top ${draft.rowClass(r.row_id) || (focus ? "bg-amber-50" : "")}`} data-testid={`surl-row-${r.row_id}`}
        data-changed={changed ? "1" : "0"} data-failed={draft.errorOf(r.row_id) ? "1" : "0"}>
      <td className="px-2 py-1">
        <RowSaveError draft={draft} id={r.row_id} testid={`surl-row-error-${r.row_id}`} />
        {ev.selected_by === "threshold" ? <span className="text-[11px]" title={L.byThreshold}>✔ {L.byThreshold}</span> : (
          <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.selected_manual} disabled={dis} data-testid={`surl-tick-${r.row_id}`}
                 onChange={(e) => patchRow(r.row_id, { selected_manual: e.target.checked })} /> {L.tick}</label>)}
      </td>
      <td className="px-2 py-1 whitespace-nowrap">{fmtDate(r.date)}</td>
      <td className="px-2 py-1">{r.voucher}</td>
      <td className="px-2 py-1">{r.party}</td>
      <td className="px-2 py-1">{r.invoice_no}</td>
      <td className="px-2 py-1 whitespace-nowrap">{r.invoice_date ? fmtDate(r.invoice_date) : L.empty}</td>
      <td className="px-2 py-1 whitespace-nowrap">{r.goods_date ? fmtDate(r.goods_date) : L.empty}</td>
      <td className="px-2 py-1 text-end tabular-nums">{fmt(r.amount)}</td>
      <td className="px-2 py-1">
        {selected && (<>
          <select value={r.relates || ""} disabled={dis} data-testid={`surl-relates-${r.row_id}`} className="rounded border px-1 py-0.5 text-xs"
                  onChange={(e) => { patchRow(r.row_id, { relates: e.target.value }); if (e.target.value === "part") partRef.current?.focus(); }}>
            {["", "yes", "no", "part"].map((k) => <option key={k} value={k}>{k === "" ? `${L.proposed}: ${L.rel[ev.proposed_relates || ""]}` : L.rel[k]}</option>)}
          </select>
          <input ref={partRef} type="number" step="0.01" placeholder={L.partAmount} value={r.part_amount ?? ""} disabled={dis}
                 data-testid={`surl-part-${r.row_id}`}
                 {...(mf ? mf.props(`part_amount.${r.row_id}`, "mt-1 block w-28 rounded border px-1 py-0.5 text-xs") : { className: "mt-1 block w-28 rounded border px-1 py-0.5 text-xs" })}
                 onChange={(e) => patchRow(r.row_id, { relates: "part", part_amount: e.target.value })} />
          <FieldMessage mf={mf} field={`part_amount.${r.row_id}`} testid={`surl-part-missing-${r.row_id}`} />
        </>)}
      </td>
      <td className="px-2 py-1">
        {selected && (m ? (
          <div data-testid={`surl-match-${r.row_id}`}>
            <div className="text-[11px]">{L.match[matchStatus]}: {m.source === "aging" ? L.fromAging : L.fromGl} · {fmtDate(m.date)} · {fmt(m.amount)}
              {m.voucher ? ` · ${m.voucher}` : ""}{m.invoice_no ? ` · ${m.invoice_no}` : ""}</div>
            {Array.isArray(m.entries) && m.entries.length > 1 &&
              <SurlEntries entries={m.entries} lang={L === TXT.ar ? "ar" : "en"} testid={`surl-match-entries-${r.row_id}`} />}
            <div className="mt-1 flex gap-2">
              {matchStatus !== "accepted" && <button type="button" disabled={dis} className="rounded border px-1 text-[11px]"
                     data-testid={`surl-accept-${r.row_id}`} onClick={() => patchRow(r.row_id, { match_decision: "accept" })}>{L.accept}</button>}
              {matchStatus !== "rejected" && <button type="button" disabled={dis} className="rounded border px-1 text-[11px]"
                     data-testid={`surl-reject-${r.row_id}`} onClick={() => patchRow(r.row_id, { match_decision: "reject" })}>{L.reject}</button>}
              {(matchStatus === "accepted" || matchStatus === "rejected") && <button type="button" disabled={dis} className="text-[11px] underline"
                     onClick={() => patchRow(r.row_id, { match_decision: "" })}>{L.undo}</button>}
            </div>
          </div>) : (
          <div data-testid={`surl-match-${r.row_id}`}>
            <span className="text-[11px]">{L.match.none}</span>
            {ev.gl_note && <div className="text-[11px] text-amber-700" data-testid={`surl-glnote-${r.row_id}`}>
              {L.glNote} ({ev.gl_note.count} · {fmt(ev.gl_note.amount)} · {fmtDate(ev.gl_note.last_date)})</div>}
          </div>))}
      </td>
      <td className="px-2 py-1 text-end tabular-nums" data-testid={`surl-omitted-${r.row_id}`}>{ev.selected ? fmt(ev.omitted_amount) : ""}</td>
      <td className="px-2 py-1"><span className={`rounded px-1.5 py-0.5 text-[11px] ${RES_CLASS[ev.result] || ""}`} data-testid={`surl-result-${r.row_id}`}>{L.res[ev.result] || ""}</span>
        <AnswerMissingNote ev={ev} testid={`surl-answer-missing-${r.row_id}`} />
        <RecordedYe ev={ev} ref_={r.recorded_ye_ref} lang={L === TXT.ar ? "ar" : "en"} disabled={dis} testid={`surl-rye-${r.row_id}`}
                    onAnswer={(ref) => patchRow(r.row_id, { recorded_ye_ref: ref })} /></td>
      <td className="px-2 py-1">
        {["omitted", "part_omitted"].includes(ev.result) && (
          <select value={r.pl_effect_kind || "expense"} disabled={dis} className="rounded border px-1 py-0.5 text-xs" data-testid={`surl-side-${r.row_id}`}
                  onChange={(e) => patchRow(r.row_id, { pl_effect_kind: e.target.value })}>
            <option value="expense">{L.expense}</option><option value="asset">{L.asset}</option>
          </select>)}
        <SurlVatFields r={r} ev={ev} dis={dis} patchRow={patchRow} />
      </td>
      <td className="px-2 py-1">
        {selected && <button type="button" disabled={dis} className="text-[11px] underline" data-testid={`surl-link-${r.row_id}`} onClick={openLink}>
          {L.link}{asArray(r.document_ids).length ? ` (${asArray(r.document_ids).length})` : ""}</button>}
        <LinkedDocsViewButton engId={engId} docIds={r.document_ids} label={r.voucher} testId={`surl-docs-view-${r.row_id}`} />
      </td>
      <td className="px-2 py-1">
        {["omitted", "part_omitted"].includes(ev.result) && (
          <div className="flex flex-col gap-1">
            {ev.needs_refresh && <span className="rounded bg-amber-100 px-1 text-[11px] text-amber-800" data-testid={`surl-refresh-${r.row_id}`}>{L.needsRefresh}</span>}
            {r.misstatement_id && !ev.needs_refresh ? <span className="text-[11px] text-emerald-700" data-testid={`surl-sent-${r.row_id}`}>✔ {L.sent}</span> : (
              <button type="button" disabled={dis} className="rounded border border-red-600 px-1 text-[11px] text-red-700" data-testid={`surl-send-${r.row_id}`}
                      onClick={() => send(r.row_id)}>{r.misstatement_id ? L.sendAgain : L.send}</button>)}
            {engId && <SurlLinkActions engId={engId} code={code} r={r} type="surl_row" dis={dis || changed} onDone={refresh} />}
          </div>)}
      </td>
    </tr>
  );
}

function Totals({ tot, L }) {
  const cells = [["tPopulation", "population"], ["tSelected", "selected"], ["tTested", "tested", true], ["tRecorded", "recorded"],
                 ["tOmitted", "omitted"], ["tSent", "sent"], ["tNext", "next_period", true], ["tConfirm", "to_confirm", true]];
  return (
    <div className="rounded border p-3" data-testid="surl-totals">
      <div className="mb-2 font-medium">{L.totals}</div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {cells.map(([lab, k, countOnly]) => (
          <div key={k} data-testid={`surl-total-${k}`}>
            <div className="text-xs text-muted-foreground">{L[lab]}</div>
            <div className="tabular-nums">{tot[`${k}_count`] ?? 0}{countOnly ? "" : ` · ${fmt(tot[`${k}_amount`])}`}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// The blocking reasons in the screen's own language (codes from the API); the API's English reasons only for an unknown code.
// The threshold's source in the screen's language; the API's English text only when no code came back.
export const thresholdSourceText = (th, L) => {
  if (th?.source_code === "clearly_trivial") return L.srcClearlyTrivial;
  if (th?.source_code === "set_by") return `${L.srcSetBy} ${th.set_by_name || L.theAuditor}`;
  return th?.source || L.empty;
};

export const gateText = (gate, L) => {
  const codes = asArray(gate?.codes);
  if (!codes.length) return asArray(gate?.reasons).join(" ");
  return codes.map((c, i) => (L.gate?.[c.code] ? L.gate[c.code].replace("{n}", String(c.n)) : asArray(gate?.reasons)[i] || "")).join(" ");
};

function Conclusion({ ws, L, draft, setDraft, patchWs, busy, gate, mf, formDirty }) {
  const v = (k) => draft[k] ?? ws[k] ?? "";
  const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setDraft((d) => ({ ...d, [k]: v })); mf?.filled(k, v); };
  const changed = ["conclusion", "conclusion_date"].filter((k) => draft[k] !== undefined);
  return (
    <div className="space-y-2 rounded border p-3" data-testid="surl-conclusion">
      <label className="block text-xs font-medium">{L.conclusion}</label>
      <textarea value={v("conclusion")} onChange={set("conclusion")} disabled={!!ws.completed} rows={3}
                {...(mf ? mf.props("conclusion", "w-full rounded border px-2 py-1 text-xs") : { className: "w-full rounded border px-2 py-1 text-xs" })}
                data-testid="surl-conclusion-text" />
      <FieldMessage mf={mf} field="conclusion" testid="surl-conclusion-missing" />
      <div className="grid gap-3 md:grid-cols-3">
        <div><label className="block text-xs">{L.conclusionDate}</label>
          <DateInput value={v("conclusion_date")} onChange={set("conclusion_date")} className="mt-1 w-full rounded border px-2 py-1 text-xs"
                     data-testid="surl-conclusion-date" /></div>
        {/* Batch 27 · C (M38): the names are the sign-off bar's — signed there, shown here, never typed */}
        <div><label className="block text-xs">{L.preparer}</label>
          <div className="mt-1 text-xs" data-testid="surl-preparer">{ws.preparer_name || L.notSigned}
            <span className="ms-1 text-muted-foreground">· {L.fromBar}</span></div></div>
        <div><label className="block text-xs">{L.reviewer}</label>
          <div className="mt-1 text-xs" data-testid="surl-reviewer">{ws.reviewer_name || L.notSigned}
            <span className="ms-1 text-muted-foreground">· {L.fromBar}</span></div></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {ws.completed ? (
          <><span className="text-xs text-emerald-700" data-testid="surl-completed">✔ {L.completed} · {fmtDate(ws.completed_at)}</span>
            <button type="button" disabled={busy} className="text-xs underline" onClick={() => patchWs({ completed: false })}>{L.reopen}</button></>
        ) : (
          <button type="button" disabled={busy || changed.length > 0 || formDirty} className="rounded border border-emerald-600 px-2 py-1 text-xs text-emerald-700"
                  data-testid="surl-complete" onClick={() => patchWs({ completed: true })}>{L.markComplete}</button>
        )}
        {!ws.completed && !gate?.ok && <span className="text-xs text-muted-foreground" data-testid="surl-gate">{L.cannotComplete} {gateText(gate, L)}</span>}
      </div>
    </div>
  );
}

function LinkDocs({ engId, row, L, onClose, onSave }) {
  const [docs, setDocs] = useState([]);
  const [sel, setSel] = useState(() => new Set(asArray(row.document_ids)));
  useEffect(() => {
    api.get(`/documents/${engId}`, { _silent: true }).then((r) => setDocs(asArray(r.data))).catch(() => setDocs([]));
  }, [engId]);
  const list = useMemo(() => docs.filter((d) => DOC_TYPES.includes(d.doc_type) || sel.has(d.id)), [docs, sel]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="surl-link-dialog">
      <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded bg-white p-4 text-sm shadow dark:bg-slate-900">
        <div className="mb-2 font-medium">{L.linkTitle} · {row.party} · {row.invoice_no || row.voucher}</div>
        {list.length === 0 && <p className="text-xs text-muted-foreground">{L.empty}</p>}
        <ul className="divide-y">{list.map((d) => (
          <li key={d.id} className="flex items-center gap-2 py-1">
            <input type="checkbox" checked={sel.has(d.id)} data-testid={`surl-doc-${d.id}`}
                   onChange={(e) => setSel((s) => { const n = new Set(s); if (e.target.checked) n.add(d.id); else n.delete(d.id); return n; })} />
            <span className="font-mono text-xs">{d.doc_ref}</span> <span className="truncate">{d.filename}</span>
            <span className="text-[11px] text-muted-foreground">{d.doc_type}</span>
          </li>))}</ul>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="rounded border px-2 py-1 text-xs" onClick={onClose}>✕</button>
          <button type="button" className="rounded border border-emerald-600 px-2 py-1 text-xs text-emerald-700" data-testid="surl-link-save"
                  onClick={() => onSave([...sel])}>{L.save}</button>
        </div>
      </div>
    </div>
  );
}
